import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const projectTypes = [
  'Kitchen Remodeling',
  'Bathroom Remodeling',
  'Interior Renovation',
  'Flooring',
  'Tile Work',
  'Painting',
  'Trim & Finish Carpentry',
  'Drywall Repair',
  'Doors & Windows',
  'Deck Repair',
  'Punch List / Make-Ready',
  'Rental Property Refresh',
  'Full Home Remodel',
  'Commercial',
  'Other'
];

export const NewJob = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    city: '',
    project_type: '',
    description: '',
    estimated_value: '',
    start_date: '',
    projected_end_date: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await api.post('/jobs', {
        ...formData,
        estimated_value: parseFloat(formData.estimated_value) || 0
      });
      toast.success('Job created successfully');
      navigate(`/portal/jobs/${response.data.id}`);
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error('Failed to create job');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-job-form">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/portal/jobs')}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </button>
        <h1 className="font-heading text-3xl">New Job</h1>
        <p className="text-gray-500 mt-1">Create a new project in your pipeline</p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="bg-white border border-gray-100 p-8 space-y-8"
      >
        {/* Client Information */}
        <div>
          <h2 className="font-heading text-xl mb-4 pb-2 border-b border-gray-100">Client Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Client Name *
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="John Smith"
                data-testid="input-client-name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Email
              </label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="client@email.com"
                data-testid="input-client-email"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="client_phone"
                value={formData.client_phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="(555) 123-4567"
                data-testid="input-client-phone"
              />
            </div>
          </div>
        </div>

        {/* Project Location */}
        <div>
          <h2 className="font-heading text-xl mb-4 pb-2 border-b border-gray-100">Project Location</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="123 Main Street"
                data-testid="input-address"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="St. Augustine"
                data-testid="input-city"
              />
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div>
          <h2 className="font-heading text-xl mb-4 pb-2 border-b border-gray-100">Project Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Project Type *
              </label>
              <select
                name="project_type"
                value={formData.project_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                data-testid="select-project-type"
              >
                <option value="">Select type</option>
                {projectTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Estimated Value
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  name="estimated_value"
                  value={formData.estimated_value}
                  onChange={handleChange}
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  placeholder="25000"
                  data-testid="input-estimated-value"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
                placeholder="Describe the project scope..."
                data-testid="input-description"
              />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div>
          <h2 className="font-heading text-xl mb-4 pb-2 border-b border-gray-100">Timeline</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                data-testid="input-start-date"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                Projected End Date
              </label>
              <input
                type="date"
                name="projected_end_date"
                value={formData.projected_end_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                data-testid="input-end-date"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => navigate('/portal/jobs')}
            className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase tracking-widest hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors disabled:opacity-50"
            data-testid="submit-job"
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? 'Creating...' : 'Create Job'}
          </button>
        </div>
      </motion.form>
    </div>
  );
};
